// utils
import {capitalize} from '@utils'
import {findFirstChildDOM} from '@/helpers/Utils'
import debounce from 'lodash/debounce'
import Settings from '@/helpers/Settings'
import VueNonreactive from '@/helpers/VueNonReactive'

// OCVueFramework Plugin
export default {
    /**
     * Vue plugin install method
     *
     * @param  {Object} Vue
     * @param  {Object} [options={}]
     *                  Example options:
     *                  {
     *                      settings: {
     *                          language: 'en',
     *                          theme: 'bootstrap-4',
     *                          rowsPerPage: 15,
     *                          timeZone: 'America/Los_Angeles', // moment js tz
     *                      },
     *                      auth: {
     *                          checkPermission: Auth.has
     *                      },
     *                      components: {
     *                          'oc-page': {
     *                              $mixin: {},
     *                              titleFormat: (title) => {
     *                                  return _appConfig.appTitle + ' | ' + title
     *                              }
     *                          }
     *                      }
     *                  }
     */
    install : function (Vue, options = {}) {

        const SETTINGS = Settings.setMultiple(options.settings)

        Vue.prototype.$settings = SETTINGS

        window.OCVueFrameworkVersion = '1.9.2'

        // moment default timeZone
        if( window.moment !== undefined && window.moment.tz !== undefined )
        {
            let defaultTimeZone = Settings.get('timeZone')
            if( defaultTimeZone === undefined ) {
                defaultTimeZone = moment.tz.guess()
            }
            moment.tz.setDefault(defaultTimeZone)
        }
        //

        // ------------- Polyfill ------------ //

        if ( typeof window.Event !== "function" )
        {
            function Event ( event, params )
            {
                params = params || { bubbles: false, cancelable: false, detail: null }
                var evt = document.createEvent( 'Event' )
                evt.initEvent( event, params.bubbles, params.cancelable, params.detail )
                return evt
            }

            window.Event = Event
        }

        // from:https://github.com/jserz/js_piece/blob/master/DOM/ChildNode/remove()/remove().md
        (function (arr) {
            arr.forEach(function (item) {
                if (item.hasOwnProperty('remove')) {
                    return
                }
                Object.defineProperty(item, 'remove', {
                    configurable: true,
                    enumerable: true,
                    writable: true,
                    value: function remove() {
                        this.parentNode.removeChild(this)
                    }
                })
            })
        })([Element.prototype, CharacterData.prototype, DocumentType.prototype])

        // --------------------------------- //

        // ------------- Import components per theme ------------ //

        let theme = SETTINGS.get('theme')

        let requireFormThemeFolder = require.context(`@/components/form/base/themes/`, true)
        let requireFormAdvancedThemeFolder = require.context(`@/components/form/advanced/themes/`, true)

        const COMPONENTS = [
            // Basic
            require('@/components/basic/text/Heading').default,
            require('@/components/basic/text/NewLine').default,
            require('@/components/basic/text/Paragraph').default,
            require('@/components/basic/Link').default,
            require('@/components/basic/List').default,

            // Common
            require('@/components/common/NoResultsFound').default,
            require('@/components/common/Icon').default,
            require('@/components/common/Action').default,
            require('@/components/common/Loader').default,
            require('@/components/common/Spinner').default,
            require('@/components/common/Button').default,
            require('@/components/ui/dashboard/Dashboard').default,
            require('@/components/ui/breadcrumb/Breadcrumb').default,
            require('@/components/ui/collapse/Collapse').default,
            require('@/components/common/Progress').default,

            // Structural
            require('@/components/structural/Page').default,
            require('@/components/structural/Row').default,
            require('@/components/structural/Col').default,
            require('@/components/structural/AccessControl').default,
            require('@/components/structural/DynamicStructure').default,
            require('@/components/structural/InlineComponent').default,
            require('@/components/structural/VNodes').default,

            // Generic
            require('@/components/plugins/PluginSlot').default,
            require('@/components/plugins/Advanced').default,

            // Widgets
            require('@/components/ui/tree/Tree').default,
            require('@/components/ui/dropdown/Dropdown').default,
            require('@/components/ui/modal/Modal').default,
            require('@/components/ui/modal/ModalButton').default,
            require('@/components/ui/alert/Alert').default,
            require('@/components/ui/tabs/Tabs').default,
            require('@/components/ui/tabs/Tab').default,
            require('@/components/ui/accordion/Accordion').default,
            require('@/components/ui/accordion/AccordionItem').default,
            require('@/components/widgets/context-menu/ContextMenu').default,
            require('@/components/widgets/audio/Audio').default,
            require('@/components/widgets/color-picker/ColorPicker').default,
            require('@/components/widgets/auto-refresher/AutoRefresher').default,
            require('@/components/widgets/icon-picker/IconPicker').default,
            require('@/components/widgets/scroll-content-paginator/ScrollContentPaginator').default,

            // Wrappers
            require('@/components/wrappers/Cleave').default,
            require('@/components/wrappers/Select2').default,
            require('@/components/wrappers/JqFileUpload').default,

            // Pagination
            require('@/components/ui/pagination/Pagination').default,
            require('@/components/ui/pagination/PaginationSummary').default,

            // Filters
            require('@/components/ui/filter/AdvancedSearch/AdvancedSearch').default,
            require('@/components/ui/filter/QueryBuilder/QueryBuilder').default,

            // Table
            require('@/components/ui/table/Table').default,
            require('@/components/ui/table/TableRow').default,
            require('@/components/ui/table/TableCell').default,
            require('@/components/ui/table/TableColumn').default,

            // Form
            require('@/components/form/Form').default,
            require('@/components/form/FormErrorSummary').default,
            require('@/components/form/base/LabelInput').default,

            // base inputs
            require('@/components/form/base/TextInput').default,
            require('@/components/form/base/TextareaInput').default,
            require('@/components/form/base/PasswordInput').default,
            require('@/components/form/base/HiddenInput').default,
            require('@/components/form/base/CheckboxInput').default,
            require('@/components/form/base/RadioInput').default,
            require('@/components/form/base/SelectInput').default,
            require('@/components/form/base/ButtonInput').default,
            require('@/components/form/base/SubmitButtonInput').default,

            // base inputs view
            requireFormThemeFolder('./' + theme + '/view/SelectInputView').default,
            requireFormThemeFolder('./' + theme + '/view/CheckboxInputView').default,

            // fields
            require('@/components/form/form-field/FormField').default,
            require('@/components/form/FormFeedback').default,
            require('@/components/form/Field').default,
            require('@/components/form/FieldView').default,

            // advanced
            require('@/components/form/advanced/CheckboxGroupInput').default,
            require('@/components/form/advanced/RadioGroupInput').default,
            require('@/components/form/advanced/datetime/DateTimeRangeInput').default,
            require('@/components/form/advanced/datetime/DateTimeInput').default,
            require('@/components/form/advanced/datetime/DateInput').default,
            require('@/components/form/advanced/datetime/TimeInput').default,
            require('@/components/form/advanced/SwitchInput').default,

            // advanced input view
            requireFormAdvancedThemeFolder('./' + theme + '/view/RadioGroupInputView').default,
            requireFormAdvancedThemeFolder('./' + theme + '/view/CheckboxGroupInputView').default
        ]

        // --------------------------------------------------------------------- //

        Vue.use(VueNonreactive)

        Vue.filter('capitalize', capitalize)

        // myDynamicClasses | mergeClassExpression ( ['static1', 'static2'] )
        Vue.filter('mergeClassExpression', function (expression, staticClasses = []) {
            let mergeClassExpression = function (classes, expression) {
                for(let n in expression) {

                    let value = expression[n]

                    if( value === undefined || value === null ) {
                        continue
                    }

                    if( Array.isArray( value ) ) {
                        let parsed = mergeClassExpression(classes, value)

                        for( let i in parsed) {
                            if( classes.indexOf(parsed[i]) === -1 )
                                classes.push(parsed[i])
                        }
                    } else if ( typeOf(value) === 'object' ) {
                        for (let name in value) {

                            let isClassActive = value[name]
                            let index = classes.indexOf(name)

                            if( ! isClassActive ) {
                                if( index !== -1 )
                                    classes.splice(classes.indexOf(name), 1)
                            } else if( classes.indexOf(name) === -1 ) {
                                classes.push(name)
                            }
                        }
                    } else { // case string

                        let stringClasses = value.split(' ').filter( v => v !== "")

                        stringClasses.forEach( stringClass => {
                            if( classes.indexOf(stringClass) === -1 )
                                classes.push(stringClass)
                        })
                    }
                }
                return classes
            }

            return mergeClassExpression(staticClasses, expression)
        })

        // v-class="['a', ['b', 'c', {e: true}], {f: false}]"
        Vue.directive('class', function (el, binding) {
            let directiveParam = [binding.value] // wrap the dir param in array, beacause it may be an objects
            let elClasses = el.className.split(' ') // take the inline html classes
            elClasses = elClasses.filter((v) => v !== "") // remove empty strings

            el.className = Vue.options.filters.mergeClassExpression(directiveParam, elClasses).join(' ')
        })

        Vue.directive('nl2br', function (el, binding) {
            el.innerHTML = nl2br(el.innerHTML)
        })

        Vue.directive('toggle', {
            // create a custom toggle handler
            inserted (el, binding, vnode, oldVnode) {
                if (binding.value != null) {
                    vnode.elm.addEventListener('click', function (e) {

                        let operation = 'toggle'
                        if( binding.modifiers.open ) operation = 'open'
                        if( binding.modifiers.close ) operation = 'close'

                        let targets = binding.value
                        if ( ! isArray(targets) ) {
                            targets = [targets]
                        }

                        for( let target of targets )
                        {
                            vnode.context.$root.$emit('oc::collapse', e, target, operation)
                        }
                    })
                }
            }
        })

        // https://medium.com/vuejs-tips/v-debounce-c382064a395d
        Vue.directive('debounce-input', {
            bind: function (el, binding) {
                let eventTarget = 'change'
                let delay = binding.value

                if( isObject(binding.value) ) {
                    if( binding.value.event !== undefined ) {
                        eventTarget = binding.value.event
                    }
                    if( binding.value.delay !== undefined ) {
                        delay = binding.value.delay
                    }
                }

                delay = parseInt(delay)
                if( isNaN(delay) ) {
                    delay = 500
                }

                let validate = function(tag, el) {
                    return tag === 'textarea' || ( tag === 'input' && el.type && el.type.toLowerCase() === 'text' )
                }

                let element = el

                if( ! validate(element.tagName.toLowerCase(), element) ) {
                    element = findFirstChildDOM(element, validate)
                }

                if ( false !== element && binding.value !== binding.oldValue ) { // change debounce only if interval has changed
                    element.oninput = debounce(function (e) {
                        if (e.isTrusted) {
                            let event = new Event(eventTarget)
                            element.dispatchEvent(event)
                        }
                    }, delay)
                }
            }
        })

        // https://jsfiddle.net/Linusborg/Lx49LaL8/
        Vue.directive('click-outside', {
            bind: function(el, binding, vNode) {
                // Provided expression must evaluate to a function.
                if (typeof binding.value !== 'function') {
                    const compName = vNode.context.name
                    let warn = `[Vue-click-outside:] provided expression '${binding.expression}' is not a function, but has to be`
                    if (compName) { warn += `Found in component '${compName}'` }

                    w(warn)
                }
                // Define Handler and cache it on the element
                const bubble = binding.modifiers.bubble
                const handler = (e) => {
                    if (bubble || (!el.contains(e.target) && el !== e.target)) {
                        binding.value(e)
                    }
                }
                el.__vueClickOutside__ = handler

                // add Event Listeners
                document.addEventListener('click', handler)
            },

            unbind: function(el, binding) {
                // Remove Event Listeners
                document.removeEventListener('click', el.__vueClickOutside__)
                el.__vueClickOutside__ = null
            }
        })

        // TODO v-tooltip directive

        // REGISTER COMPONENTS
        let checkPermission = (permission) => true
        if( options.auth && options.auth.checkPermission ) {
            checkPermission = options.auth.checkPermission
        }

        let componentMixins = [
            {
                methods: {
                    $checkPermission: checkPermission
                }
            }
        ]

        COMPONENTS.forEach(component => {

            // apply component mixin and props default values from plugin options
            if( options.components && options.components[component.name] ) {

                let componentOptions = options.components[component.name]

                Object.keys(componentOptions).forEach( prop => {

                    // extract component mixin form options
                    if( prop === '$mixin' && isObject(componentOptions[prop]) ) {
                        componentMixins.push(
                            componentOptions[prop]
                        )

                        return
                    }

                    // change default values
                    if( ! isArray(component.props) && component.props[prop] === undefined ) {
                        throw new Error(`[OCVueFramework] Error while changing default value for prop(${prop}) of component(${component.name}). Prop is not difined. You should use "$mixin" instead.`)
                    }

                    if( isArray(component.props) || ! component.props[prop].hasOwnProperty('type') ) {
                        throw new Error(`[OCVueFramework] Error while changing default value for prop(${prop}) of component(${component.name}). This prop is defined without ability to set a default value. You should use "$mixin" instead.`)
                    }

                    component.props[prop].default = componentOptions[prop]
                })
            }
            //

            if( component.mixins === undefined ) {
                component.mixins = componentMixins
            } else if ( isArray(component.mixins) ) {
                component.mixins.push(...componentMixins)
            }

            Vue.component(component.name, component)
        })

        // v-confirm / dialog widget
        let Dialog = require('@/components/widgets/dialog/Dialog').default

        // we need oc-modal component to be registered
        let dialogInstance = new Dialog(Vue)

        Vue.prototype.$dialog = dialogInstance

        // Usage:
        // v-confirm
        // v-confirm="Are you sure?"
        // v-confirm="{message: 'Are you sure?', ok: myMethod, cancel: myCancelMethod}"
        let ConfirmDirective = require('@/components/widgets/dialog/directives/ConfirmDirective').default
        Vue.directive('confirm', ConfirmDirective.getDefinition(dialogInstance))
        //

    }
}
